import { useSupportGroupOptions } from "../../../../hooks/useSupportGroups";
import { DatePicker, Select } from "@rio-cloud/rio-uikit";
import { useRef } from "react";
import moment, { Moment } from "moment";
import { FormFields } from '../../../../models/newSupportCall';
import useNewSupportCall from '../../../../hooks/useNewSupportCall';
import { formatPhoneNumber } from '../../../../utils/phoneUtils';
import { fetchDealershipsByDN } from '../../../../api/fetchDealerships';
import SupportCallDnField from "./SupportCallDnField";
import { formatEntryDate } from "../../entryDate";
import { useIntl } from "react-intl";

export interface SupportCallOccurrenceFieldsProps {
    formData: FormFields;
    errors: Partial<Record<keyof FormFields, string>>;
    updateField: <K extends keyof FormFields>(field: K, value: FormFields[K]) => void;
    name: string;
    fetchVehicle: (chassis: string) => Promise<void>;
    subject: string;
    occurrenceId?: string;
}

const fieldColumnClassName = 'form-group form-group-lg flex-1-1';
const rowClassName = 'display-flex gap-20 width-100pct';

const FieldError = ({ error }: { error?: string }) => (
    <div className="height-15">
        {error ? <span className='text-size-12 text-color-danger'>{error}</span> : null}
    </div>
);

const SupportCallOccurrenceFields = ({
    formData,
    errors,
    updateField,
    name,
    fetchVehicle,
    subject,
    occurrenceId,
}: SupportCallOccurrenceFieldsProps) => {
    const intl = useIntl();

    const { regionalOptions, dnOptions, getAllDns, role } = useNewSupportCall(formData, updateField);
    const groupOptions = useSupportGroupOptions();
    const regionalLookupRequest = useRef(0);
    const isTechnicalSubject = subject === 'TECHNICAL';
    const isTechnicalSupport = formData.supportType === 'TECHNICAL_SUPPORT';

    const requiredPlaceholder = isTechnicalSubject || isTechnicalSupport ? intl.formatMessage({ id: 'intl-msg:occurrence.required' }) : intl.formatMessage({ id: 'intl-msg:occurrence.optional' });

    const technicalLabel = (label: string) => isTechnicalSubject ? `${label} *` : label;
    const entryDateLabel = intl.formatMessage({ id: 'intl-msg:tas.occurrence.entryDateTime' }) + (isTechnicalSubject || isTechnicalSupport ? ' *' : '');
    const dnResponsibleLabel = isTechnicalSubject || isTechnicalSupport
        ? intl.formatMessage({ id: 'intl-msg:tas.occurrence.dnResponsible' }) + ' *'
        : intl.formatMessage({ id: 'intl-msg:tas.occurrence.dnResponsible' });
    const odometerLabel = isTechnicalSubject || isTechnicalSupport ? 'Km *' : 'Km';

    const chassisLabel = isTechnicalSubject || isTechnicalSupport
        ? `${intl.formatMessage({ id: 'intl-msg:tas.occurrence.chassis' })} *`
        : intl.formatMessage({ id: 'intl-msg:tas.occurrence.chassis' });

    const licensePlateLabel = isTechnicalSubject || isTechnicalSupport
        ? `${intl.formatMessage({ id: 'intl-msg:tas.occurrence.licensePlate' })} *`
        : intl.formatMessage({ id: 'intl-msg:tas.occurrence.licensePlate' });


    const odometerPlaceholder = isTechnicalSubject || isTechnicalSupport ? intl.formatMessage({ id: 'intl-msg:occurrence.required' }) : requiredPlaceholder;

    const updateDnAndRegional = async (value: string) => {
        updateField('dn', value);

        const requestId = ++regionalLookupRequest.current;
        const dn = value.match(/^DN:\s*([^|]+)/i)?.[1]?.trim();

        if (!dn) {
            return;
        }

        const dealership = await fetchDealershipsByDN(dn);
        if (requestId !== regionalLookupRequest.current || !dealership?.regional) {
            return;
        }
 
        const normalizedRegional = dealership.regional.trim().toLocaleLowerCase();

        const regional = regionalOptions.find(option => {
            const formattedLabel = option.label.replace(/\D/g, '');
            return formattedLabel === normalizedRegional;
        });

        if (regional) {
            updateField('regionalId', regional.id);
        }
    };

    const updateChassis = (value: string) => {
        updateField('chassis', value);
        if (value.length === 17) {
            fetchVehicle(value);
        }
    };

    return (
        <div className='padding-15 rounded border'>
            <div className={rowClassName}>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{intl.formatMessage({ id: 'intl-msg:tas.occurrence.responsible' })}*</label>
                    <input
                        type='text'
                        className='form-control'
                        placeholder={intl.formatMessage({ id: 'intl-msg:occurrence.required' })}
                        value={name || formData.responsible}
                        disabled={Boolean(name?.trim())}
                        onChange={event => updateField('responsible', event.target.value)}
                    />
                    <FieldError error={errors.responsible} />
                </div>
                <SupportCallDnField
                    dn={formData.dn}
                    error={errors.dn}
                    role={role}
                    dnOptions={dnOptions}
                    getAllDns={getAllDns}
                    onChange={updateDnAndRegional}
                />
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{chassisLabel}</label>
                    <input
                        type='text'
                        className='form-control'
                        placeholder={requiredPlaceholder}
                        value={formData.chassis}
                        onChange={event => updateChassis(event.target.value)}
                        maxLength={17}
                        disabled={!!occurrenceId}
                    />
                    <FieldError error={errors.chassis} />
                </div>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{licensePlateLabel}</label>
                    <input
                        type='text'
                        className='form-control'
                        value={formData.licensePlate}
                        onChange={event => updateField('licensePlate', event.target.value)}
                        placeholder={requiredPlaceholder}
                        disabled={!!occurrenceId}
                    />
                    <FieldError error={errors.licensePlate} />
                </div>
            </div>
            <div className={rowClassName}>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{dnResponsibleLabel}</label>
                    <input
                        type='text'
                        className='form-control'
                        value={formData.dnResponsible}
                        onChange={event => updateField('dnResponsible', event.target.value)}
                        placeholder={isTechnicalSubject || isTechnicalSupport ? intl.formatMessage({ id: 'intl-msg:occurrence.required' }) : requiredPlaceholder}
                    />
                    <FieldError error={errors.dnResponsible} />
                </div>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{intl.formatMessage({ id: 'intl-msg:tas.occurrence.phone' })} *</label>
                    <input
                        type='text'
                        className='form-control'
                        placeholder={intl.formatMessage({ id: 'intl-msg:occurrence.required' })}
                        value={formData.phone}
                        onChange={event => updateField('phone', formatPhoneNumber(event.target.value))}
                    />
                    <FieldError error={errors.phone} />
                </div>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{technicalLabel(intl.formatMessage({ id: 'intl-msg:tas.occurrence.customerName' }))}</label>
                    <input
                        type='text'
                        className='form-control'
                        value={formData.customer}
                        onChange={event => updateField('customer', event.target.value)}
                        placeholder={requiredPlaceholder}
                    />
                    <FieldError error={errors.customer} />
                </div>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{intl.formatMessage({ id: 'intl-msg:tas.occurrence.regional' })} *</label>
                    <Select
                        bsSize='lg'
                        value={formData.regionalId ? [formData.regionalId] : []}
                        onChange={option => updateField('regionalId', option?.id || '')}
                        options={regionalOptions}
                        placeholder={intl.formatMessage({ id: 'intl-msg:tas.occurrence.selectRegional' })}
                        useClear
                    />
                    <FieldError error={errors.regionalId} />
                </div>
            </div>
            <div className={rowClassName}>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{technicalLabel(intl.formatMessage({ id: 'intl-msg:tas.occurrence.hourMeter' }))}</label>
                    <input
                        type='text'
                        className='form-control'
                        value={formData.hourMeter}
                        onChange={event => updateField('hourMeter', event.target.value)}
                        placeholder={requiredPlaceholder}
                    />
                    <FieldError error={errors.hourMeter} />
                </div>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{odometerLabel}</label>
                    <input
                        type='text'
                        className='form-control'
                        value={formData.odometer}
                        onChange={event => updateField('odometer', event.target.value)}
                        placeholder={odometerPlaceholder}
                    />
                    <FieldError error={errors.odometer} />
                </div>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{entryDateLabel}</label>
                    <DatePicker
                        id='support-call-entry-date'
                        inputProps={{ placeholder: intl.formatMessage({ id: 'intl-msg:tas.occurrence.selectDate' }) }}
                        dateFormat='DD/MM/YYYY'
                        timeFormat='HH:mm'
                        onChange={(result: Moment | string) => updateField('entryDate', result)}
                        isValidDate={(currentDate: Moment) => currentDate.isSameOrBefore(moment(), 'day')}
                        className="margin-0"
                        value={formatEntryDate(formData.entryDate)}
                    />
                    <FieldError error={errors.entryDate} />
                </div>
            </div>
            <div className={rowClassName}>
                <div className={fieldColumnClassName}>
                    <label className='text-size-14 text-color-darker'>{intl.formatMessage({ id: 'intl-msg:tas.occurrence.group' })}</label>
                    <Select
                        bsSize='lg'
                        value={formData.assignedGroupId ? [formData.assignedGroupId] : []}
                        onChange={option => updateField('assignedGroupId', option?.id || '')}
                        options={groupOptions}
                        placeholder={intl.formatMessage({ id: 'intl-msg:tas.occurrence.selectGroup' })}
                        useClear
                    />
                </div>
            </div>
        </div>
    );
};

export default SupportCallOccurrenceFields;
